/**
 * Sessão do painel — ver `../backend/docs/SEGURANCA.md`.
 *
 * REGRA INEGOCIÁVEL: o access token (JWT HS256 de 15 min) vive **apenas em memória**, numa closure
 * deste módulo. Nunca em `localStorage`, `sessionStorage`, IndexedDB, cookie legível ou variável
 * global — um XSS não pode simplesmente ler a sessão, e um F5 derruba o token de propósito (o
 * refresh silencioso o recria).
 *
 * O refresh token é **opaco** e fica no cookie httpOnly `triplice_refresh`, que o JS não lê nem
 * escreve: ele viaja sozinho porque o axios usa `withCredentials: true`. O servidor guarda só o
 * sha256 dele, rotativo e com detecção de reuso.
 *
 * Contra CSRF, `refresh` e `logout` usam double-submit: o backend também grava o cookie **legível**
 * `triplice_csrf` e exige o mesmo valor no header `X-CSRF-Token` (ver `src/lib/api.ts`).
 */
import { createContext, use } from 'react';
import type { Permissao, Usuario } from '@/tipos/api';

// ---------------------------------------------------------------------------
// Store em memória (closure de módulo)
// ---------------------------------------------------------------------------

let accessToken: string | null = null;

export function definirAccessToken(token: string | null): void {
  accessToken = token;
}

export function obterAccessToken(): string | null {
  return accessToken;
}

export function limparAccessToken(): void {
  accessToken = null;
}

// ---------------------------------------------------------------------------
// Aviso de sessão encerrada (o interceptor de 401 dispara; o AuthProvider ouve)
// ---------------------------------------------------------------------------

type OuvinteEncerramento = () => void;

const ouvintes = new Set<OuvinteEncerramento>();

/** Registra um ouvinte e devolve a função de cancelamento. */
export function aoEncerrarSessao(ouvinte: OuvinteEncerramento): () => void {
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
  };
}

/** Limpa o token da memória e notifica quem estiver ouvindo (ex.: redirecionar ao login). */
export function encerrarSessaoLocal(): void {
  limparAccessToken();
  for (const ouvinte of [...ouvintes]) ouvinte();
}

// ---------------------------------------------------------------------------
// Aviso de senha nova obrigatória (o interceptor de 403 dispara; o AuthProvider ouve) — 1.8
// ---------------------------------------------------------------------------

/**
 * A mensagem do 403 do `JwtAuthGuard` quando a pessoa tem `exigir_nova_senha` (regra 24).
 *
 * O caso que ela cobre: o admin marca "exigir senha nova" com a pessoa **no meio do uso**. A sessão já
 * carregada não sabe disso; a próxima chamada recebe este 403, o app relê a sessão e a `RotaProtegida`
 * leva à tela bloqueante. Tem de ser igual à de `ErroNovaSenhaObrigatoria` no backend.
 */
export const MENSAGEM_NOVA_SENHA_OBRIGATORIA = 'Defina uma senha nova para continuar.';

/** A tela bloqueante da senha nova obrigatória (fora da Shell: sem menu, sem atalho). */
export const CAMINHO_TROCAR_SENHA = '/trocar-senha';

const ouvintesNovaSenha = new Set<() => void>();

export function aoExigirNovaSenha(ouvinte: () => void): () => void {
  ouvintesNovaSenha.add(ouvinte);
  return () => {
    ouvintesNovaSenha.delete(ouvinte);
  };
}

export function avisarNovaSenhaObrigatoria(): void {
  for (const ouvinte of [...ouvintesNovaSenha]) ouvinte();
}

// ---------------------------------------------------------------------------
// Contexto de autenticação
// ---------------------------------------------------------------------------

export interface EstadoAuth {
  usuario: Usuario | null;
  autenticado: boolean;
  /**
   * `true` enquanto a sessão inicial não terminou (refresh silencioso + `GET /auth/sessao`). A
   * `RotaProtegida` mostra o loader da marca nesse intervalo em vez de mandar para o login quem tem
   * cookie válido — sem isto, o F5 deslogaria.
   */
  carregando: boolean;
  /** Permissões efetivas do usuário, como o backend as resolveu. Vazio quando anônimo. */
  permissoes: Permissao[];
  /** `true` se o usuário tem a permissão. **Negado por padrão**: sem sessão, sempre `false`. */
  tem: (permissao: Permissao) => boolean;
  /** Chamado após login bem-sucedido: guarda o token em memória e o usuário no estado. */
  definirSessao: (dados: { accessToken: string; usuario: Usuario }) => void;
  /** Revoga a família do refresh (`POST /auth/logout`) e só então limpa o estado local. */
  encerrarSessao: () => Promise<void>;
  /**
   * Relê `GET /auth/sessao` e atualiza o usuário. É o que faz uma permissão recém-concedida (ou
   * revogada) chegar ao menu sem exigir novo login. Devolve o usuário relido, ou `null` se falhou.
   */
  recarregarSessao: () => Promise<Usuario | null>;
}

export const ContextoAuth = createContext<EstadoAuth | null>(null);

export function useAuth(): EstadoAuth {
  const contexto = use(ContextoAuth);
  if (!contexto) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return contexto;
}

/**
 * Curinga dos papéis **fixos**: `master` e `admin` recebem `['*']` do backend em vez de uma cópia de
 * todas as chaves, para que uma permissão nova nasça já valendo para eles.
 */
export const PERMISSAO_TODAS = '*';

/**
 * Chaves que o curinga **não** cobre (ADR 0015). O `admin` tem `['*']` e mesmo assim não as alcança;
 * só o `master`, que as recebe nominalmente.
 *
 * 🚨 Cópia deliberada da constante de `backend/src/auth/guards/permissao.guard.ts`. O frontend não
 * importa do backend (repos separados), então a duplicação é o preço — e o que a torna segura é que
 * **errar aqui só esconde um botão a mais**, nunca libera: a tranca que vale é o `@Permissao()` do
 * servidor.
 */
export const PERMISSOES_RESERVADAS: readonly Permissao[] = [
  'usuarios.conceder_admin',
  'master.manutencao',
];

/**
 * Chaves de **concessão reservada ao master** (ADR 0026): qualquer papel pode tê-las — inclusive o
 * `admin` —, mas só o master as concede, e o curinga **não** as cobre. Sem esta lista, o `['*']` do
 * admin faria o painel mostrar a ele o item "Bases do MM Hays" e as telas do MM Hays, que o servidor
 * negaria com 403 (as `mm_hays.*` só valem nominalmente, `satisfazPermissao` do backend).
 *
 * 🚨 Mesma cópia deliberada de `PERMISSOES_RESERVADAS`, pelo mesmo motivo: repos separados.
 */
export const PERMISSOES_DE_CONCESSAO_DO_MASTER: readonly Permissao[] = [
  'mm_hays.conversar',
  'mm_hays.bases_ver',
  'mm_hays.bases_gerir',
  'mm_hays.documentos_enviar',
  'mm_hays.documentos_publicar',
  'mm_hays.documentos_desativar',
  // 13.15: escolher o modelo de IA. Além da chave, o servidor exige SER o master (ADR 0038).
  'mm_hays.configurar',
];

/**
 * Autorização por **permissão**, nunca por papel: o papel é configurável pelo admin, então uma tela
 * que perguntasse "é admin?" passaria a mentir no dia em que um papel novo recebesse a permissão.
 *
 * 🚨 **O curinga precisa ser expandido aqui.** O backend manda `['*']` para os papéis fixos, e um
 * `includes` cru diria que o admin não tem `usuarios.ver` — ele veria o menu vazio e levaria um
 * redirecionamento ao abrir a própria tela que administra. A regra é a mesma do `satisfazPermissao`
 * do servidor: o curinga cobre tudo **menos** as chaves reservadas (ADR 0015) e as de concessão do
 * master (ADR 0026).
 *
 * Começa fechado: usuário ausente é `false`. E esconder no frontend **não** substitui o
 * `@Permissao()` do backend — lá é a tranca, aqui é só não mostrar à pessoa um botão que ela não
 * pode usar.
 */
export function temPermissao(usuario: Usuario | null, permissao: Permissao): boolean {
  if (!usuario) return false;
  if (usuario.permissoes.includes(permissao)) return true;
  if (PERMISSOES_RESERVADAS.includes(permissao)) return false;
  // ADR 0026: o curinga do admin não alcança chave de concessão do master; ela só vale nominalmente.
  if (PERMISSOES_DE_CONCESSAO_DO_MASTER.includes(permissao)) return false;
  return usuario.permissoes.includes(PERMISSAO_TODAS);
}
